/**
 * Reglages de rendu du moteur de dessin.
 *
 * C'EST LE SEUL FICHIER A TOUCHER pour ajuster le rendu (feathering, grain,
 * lissage, seuils de capture). Aucune de ces valeurs ne circule sur le reseau :
 * ce sont des choix de rendu local, contrairement aux constantes de shared/ qui
 * font partie du contrat de jeu.
 */

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/** Largeur du canvas offscreen de la zone. La hauteur suit le ratio de la zone. */
export const ZONE_CANVAS_WIDTH = 1024;

/**
 * Adoucissement de la retouche, EN PIXELS DU TABLEAU SOURCE.
 *
 * Le probleme qu'il resout : agrandi a l'ecran, le tableau est interpole, donc
 * ses bords s'etalent sur environ un pixel source. La retouche, elle, est tracee
 * a haute resolution et reste nette au pixel. Le devineur n'a alors pas besoin
 * de regarder le dessin : il cherche la seule zone piquee de l'image.
 *
 * La reponse EVIDENTE (construire le calque a la resolution de la source) est
 * mauvaise : elle ajoute une reduction destructrice avant l'agrandissement, soit
 * deux reechantillonnages la ou le tableau n'en subit qu'un. La retouche ressort
 * plus grossiere que son entourage, et aucun reglage de zoom ne rattrape ca.
 *
 * On garde donc un calque haute resolution (un seul reechantillonnage, comme le
 * tableau) et on reproduit la seule chose qui manque : l'etalement du a
 * l'interpolation. Exprime en pixels SOURCE, il est independant du zoom.
 *
 *   0    retouche nette au pixel : elle se repere a sa nettete
 *   0.5  legerement adoucie, encore un peu plus nette que l'œuvre
 *   1    etalement d'un pixel source, l'equivalent d'une interpolation bilineaire
 *   2    franchement plus floue que l'œuvre
 */
export const ZONE_SOFTNESS_SOURCE_PX = 0.3;

/**
 * Plafond du devicePixelRatio pour le canvas ecran. Au-dela de 2 le cout en
 * pixels double sans gain visible, et c'est la premiere chose qui fait tomber
 * sous les 60 fps sur mobile.
 */
export const MAX_DEVICE_PIXEL_RATIO = 2;

// ---------------------------------------------------------------------------
// Fusion zone / tableau
// ---------------------------------------------------------------------------

/**
 * Rayon du flou applique au masque de la zone, en pixels du canvas composite
 * (donc en pixels de l'image source). Le but est de tuer l'arete franche du
 * rectangle sans faire baver la retouche.
 * 0 = bord net. Au-dela de ~4 le halo devient visible sur les aplats clairs.
 */
export const FEATHER_PX = 2;

/**
 * Grain applique sur TOUT le tableau composite, pour que la retouche et l'œuvre
 * partagent la meme texture. C'est ce qui fait le plus pour masquer la couture.
 */
export const GRAIN_ALPHA = 0.04;
export const GRAIN_TILE_SIZE = 256;
/** Amplitude du bruit autour du gris neutre (0-127). Plus haut = grain plus dur. */
export const GRAIN_AMPLITUDE = 64;
/** 'overlay' preserve les couleurs ; 'soft-light' est plus doux, 'source-over' plus plat. */
export const GRAIN_BLEND: GlobalCompositeOperation = 'overlay';
/** Seed du bruit : le grain est identique d'une session a l'autre. */
export const GRAIN_SEED = 0x9e3779b9;

// ---------------------------------------------------------------------------
// Pinceau
// ---------------------------------------------------------------------------

/**
 * Distance a parcourir A L'ECRAN avant qu'un point ne soit retenu.
 *
 * C'est le terme de REACTIVITE : en dessous, le geste ne laisse aucune trace.
 * 4 px, soit environ un millimetre : le trait se declenche des qu'on bouge, y
 * compris pour tourner autour d'un point en cherchant un detail.
 *
 * Il etait exprime en coordonnees ZONE, et c'etait le probleme d'origine : la
 * distance a parcourir grandissait alors avec le zoom, exactement quand on
 * cherche de la precision.
 */
export const POINT_SPACING_PX = 4;

/**
 * Plancher de fidelite, en fraction de la LARGEUR DU PINCEAU.
 *
 * Echantillonner plus fin qu'un quart de pinceau n'ajoute rien de visible : le
 * bout rond recouvre l'ecart. C'est donc ici, et pas dans une constante fixe,
 * que se borne le nombre de points : un gros pinceau se contente de peu, un
 * pinceau fin en demande beaucoup, et le cout suit le besoin reel.
 *
 * La version precedente bornait en coordonnees zone (0,004). A fort zoom cela
 * redevenait une grande distance a l'ecran (une vingtaine de pixels), ce qui
 * reintroduisait le defaut qu'on cherchait a corriger, precisement dans le cas
 * « zoome + petit pinceau ».
 */
export const POINT_SPACING_BRUSH_RATIO = 0.25;

/**
 * Plafond : l'ANCIENNE valeur fixe. Sur un petit ecran la zone occupe peu de
 * pixels, et un gros pinceau demanderait un pas plus large qu'avant : le trait
 * serait plus grossier qu'aujourd'hui, et un dessin fait au telephone perdrait
 * en finesse une fois regarde sur grand ecran. Le reglage ne peut donc
 * qu'ameliorer la fidelite, jamais la degrader.
 *
 * Cout : un point pese 4 octets, la taille varie comme l'inverse du pas, et
 * `fitToBudget` decime avant l'envoi si les 32 Ko sont depasses. Un dessin
 * typique passe de 7 a 11 Ko.
 */
export const MAX_POINT_DISTANCE = 0.012;

/**
 * Bornes du slider de taille de pinceau, en unites zone (cf. ZONE_UNITS).
 * En dessous de 2 le trait disparait au rendu ; au-dela de 60 il couvre plus
 * du quart de la zone et le pinceau devient un pot de peinture.
 */
export const MIN_BRUSH_SIZE = 2;
export const MAX_BRUSH_SIZE = 60;

/**
 * Tolerance du pot de peinture, en distance de canal (0-255).
 *
 * Elle reste a 0, et ce n'est plus un compromis : le lisere qu'elle laissait le
 * long des traits est traite autrement, par `FILL_BLEED_PX` ci-dessous. Monter
 * la tolerance etait la fausse bonne idee : elle ne fait que deplacer le
 * probleme, en mangeant les pixels les plus transparents du bord (le trait
 * durcit et perd le fondu qu'on veut garder) tout en laissant intacts ceux
 * dont l'opacite depasse le seuil. Le lisere devient plus fin, il ne disparait
 * pas.
 */
export const FILL_TOLERANCE = 0;

/**
 * De combien le remplissage se glisse SOUS le bord des traits, en pixels du
 * canvas de zone.
 *
 * Le probleme : un trait de pinceau est antialiase, son bord est une bande de
 * pixels a opacite partielle. Le remplissage s'arretait au premier d'entre eux,
 * et le fond de la zone continuait de transparaitre a travers cette bande : un
 * lisere clair, tout le long de chaque trait touche.
 *
 * La correction ne peint pas PAR-DESSUS ces pixels, elle peint DESSOUS
 * (`destination-over` fait a la main) : chaque pixel de bord devient
 * `trait x opacite + remplissage x (1 - opacite)`, c'est-a-dire exactement ce
 * qu'il vaudrait si le remplissage avait ete pose en premier. Le fondu du trait
 * est conserve au pixel pres, seul le fond qu'il laissait voir change.
 *
 * Cette regle rend la bavure SANS DANGER, et c'est ce qui permet de ne pas se
 * poser de question sur sa largeur : un pixel deja opaque n'est jamais modifie.
 * Meme si la bavure traversait un trait fin de part en part, elle ne ferait que
 * poser la bonne couleur derriere lui. Le plus petit pinceau fait de toute
 * facon 16 px sur ce canvas (BRUSH_SIZES[0] de 256 unites, canvas de 1024), il
 * n'y a donc rien a traverser.
 */
export const FILL_BLEED_PX = 2;

/**
 * Marge, en pixels D'ECRAN, autour de la zone a redessiner : au-dela, le
 * pinceau et le pot deplacent la vue au lieu de peindre.
 *
 * Le basculement se faisait sur le liseré lui-meme, au pixel pres. Or c'est
 * justement le long du bord qu'on travaille le plus : un trait pose sur la
 * limite partait un peu au-dela, et le geste devenait un deplacement de vue
 * alors qu'on croyait dessiner. Une marge rend la frontiere franchissable a la
 * main plutot qu'a la souris.
 *
 * En pixels d'ECRAN, donc convertis au zoom courant : la tolerance doit se
 * ressentir pareil de pres comme de loin. En pixels d'image, elle vaudrait
 * quelques millimetres a fort grossissement et la moitie de la zone en vue
 * d'ensemble.
 */
export const PAN_MARGIN_PX = 20;

// ---------------------------------------------------------------------------
// Sequence de la phase de dessin
// ---------------------------------------------------------------------------

/** Vue d'ensemble avant l'auto-zoom. */
export const OVERVIEW_MS = 2000;
/** Duree de l'auto-zoom (fourchette "ecrans" du design system : 250-350 ms). */
export const ZOOM_ANIM_MS = 320;
/** Periode de pulsation du lisere or. */
export const PULSE_PERIOD_MS = 1600;
/** Duree de la revelation de la zone en phase de devinette (600-800 ms). */
export const REVEAL_MS = 700;

/** Easing du design system : cubic-bezier(0.2, 0.8, 0.2, 1). */
export const EASE = { x1: 0.2, y1: 0.8, x2: 0.2, y2: 1 } as const;

// ---------------------------------------------------------------------------
// Viewport
// ---------------------------------------------------------------------------

/** Marge autour du tableau en vue d'ensemble. */
export const FIT_PADDING = 0.92;
/** Bornes de zoom, en multiples de l'echelle "tableau entier a l'ecran". */
export const MIN_ZOOM_FACTOR = 0.8;
export const MAX_ZOOM_FACTOR = 12;
